/**
 * The rows of a grid.
 *
 * A row is a question in its own right — it has a `key`, it is answered on its own, and it
 * becomes its own column in the CSV. What it does **not** have is a scale: it borrows the parent's
 * options, its config and its step (R25), which is why there is nothing here but a code, a type, a
 * label and whether it is required.
 *
 * One level only. A row never holds rows.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTButton, UTTextField } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { MatrixRowType, SurveyLanguage } from '../../../Types/Surveys.types';
import { MATRIX_ROW_TYPES, SURVEY_LIMITS } from '../../../Types/Surveys.types';
import type { SurveyRowDraft } from '../../../Utils/surveyForm';
import { BASE_LANGUAGE } from '../../../Utils/surveyForm';
import { QUESTION_TYPE_LABELS } from '../../../Utils/surveyFormat';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const titleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  flex: 1 1 240px;
  min-width: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const rowStyle = css`
  display: grid;
  grid-template-columns: 26px minmax(0, 200px) minmax(0, 1fr) 140px auto auto;
  align-items: center;
  gap: var(--space-s);
`;

const indexStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-align: right;
`;

const selectStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-xs);
  min-width: 0;
`;

const flagStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
  cursor: pointer;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
`;

const countStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface MatrixRowsEditorProps {
  id: string;
  rows: SurveyRowDraft[];
  language: SurveyLanguage;
  frozen: boolean;
  onKeyChange: (rowUid: string, value: string) => void;
  onLabelChange: (rowUid: string, value: string) => void;
  onTypeChange: (rowUid: string, type: MatrixRowType) => void;
  onRequiredChange: (rowUid: string, value: boolean) => void;
  onAdd: () => void;
  onRemove: (rowUid: string) => void;
}

export const MatrixRowsEditor = ({
  id,
  rows,
  language,
  frozen,
  onKeyChange,
  onLabelChange,
  onTypeChange,
  onRequiredChange,
  onAdd,
  onRemove,
}: MatrixRowsEditorProps): JSX.Element => (
  <div className={wrapStyle}>
    <div className={headStyle}>
      <span className={titleStyle}>Linhas da grade</span>
      <p className={hintStyle}>
        Cada linha é respondida separadamente contra a escala acima e vira uma coluna própria no
        CSV, com o código da linha. A pergunta mãe não gera coluna nenhuma, porque ninguém a
        responde.
      </p>
    </div>

    {rows.map((row, index) => (
      <div key={row.uid} className={rowStyle}>
        <span className={indexStyle}>{index + 1}</span>

        <UTTextField
          id={`${id}-row-key-${index}`}
          placeholder='codigo_da_linha'
          value={row.key}
          disabled={frozen || language !== BASE_LANGUAGE}
          onChange={event => {
            onKeyChange(row.uid, event.target.value);
          }}
        />

        <UTTextField
          id={`${id}-row-label-${index}`}
          placeholder='O que esta linha avalia'
          value={row.labels[language]}
          onChange={event => {
            onLabelChange(row.uid, event.target.value);
          }}
        />

        <select
          id={`${id}-row-type-${index}`}
          className={selectStyle}
          value={row.type}
          aria-label={`Tipo da linha ${index + 1}`}
          onChange={event => {
            onTypeChange(row.uid, event.target.value as MatrixRowType);
          }}
        >
          {MATRIX_ROW_TYPES.map(type => (
            <option key={type} value={type}>
              {QUESTION_TYPE_LABELS[type]}
            </option>
          ))}
        </select>

        <label className={flagStyle} htmlFor={`${id}-row-required-${index}`}>
          <input
            id={`${id}-row-required-${index}`}
            type='checkbox'
            checked={row.isRequired}
            onChange={event => {
              onRequiredChange(row.uid, event.target.checked);
            }}
          />
          obrigatória
        </label>

        <div className={actionsStyle}>
          <IconButton
            id={`${id}-row-remove-${index}`}
            icon='Misc.Trash'
            size='sm'
            variant='danger'
            aria-label={`Remover a linha ${index + 1}`}
            onClick={() => {
              onRemove(row.uid);
            }}
          />
        </div>
      </div>
    ))}

    <div className={footerStyle}>
      <UTButton
        fitContent
        variant='secondary'
        size='small'
        id={`${id}-row-add`}
        text='Nova linha'
        icon='Arrows.Plus'
        disabled={rows.length >= SURVEY_LIMITS.rowsPerMatrix}
        onClick={onAdd}
      />
      <span className={countStyle}>
        {rows.length} de {SURVEY_LIMITS.rowsPerMatrix}
        {frozen ? ' · códigos travados: a pesquisa já tem resposta' : ''}
      </span>
    </div>
  </div>
);
